import { useMutation } from '@apollo/client';
import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';
import { ProductionOrganizationInfoInput } from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import DataList from '../common/list/DataList';
import { getKeyOrganizationInput, ProductionOrganizationInfoWithKey } from './helpers/organizationUtils';
import { addOrganizationToProduction, client } from './helpers/production';
import { useSaveKeyOrganizations } from './helpers/productionApi';
import KeyOrganizationsTitle from './KeyOrganizationsTitle';

type ListProps = {
    id: string;
    loading: boolean;
    data: any;
    entity: string;
    feature: string;
    productionId: string;
    setIsCreate: Dispatch<SetStateAction<boolean>>;
    setSearchAllOrganization: Dispatch<SetStateAction<boolean>>;
    setQuery: Dispatch<SetStateAction<string>>;
    orgWithKey: ProductionOrganizationInfoWithKey[];
    emptyListElement: React.ReactNode;
};

export default ({
    id,
    loading,
    data,
    feature,
    orgWithKey,
    productionId,
    emptyListElement,
    setSearchAllOrganization,
    setQuery
}: ListProps) => {
    const { activeOrganizationAccount } = useContext(UserContext);
    const [isOrganizationSaving, setIsOrganizationSaving] = useState({ id: '', isSaving: false, productionRoles: [] });
    const [saveOrganizationToProduction] = useMutation(addOrganizationToProduction, { client });
    const { saveKeyOrganization } = useSaveKeyOrganizations();
    // Filter out organizations that are already associated with the production
    const filteredData = useMemo(() => {
        const associatedOrganizationIds = orgWithKey.map((p) => p?.organizationReference?.id);

        if (data?.results?.hits?.items) {
            return data.results.hits.items
                .filter((org) => !associatedOrganizationIds.includes(org?.reference?.id))
                .map((o) => {
                    const { reference, name, hierarchicalCategories, organizationTypes } = o;
                    return {
                        name,
                        organizationReference: reference,
                        organizationCategories: hierarchicalCategories,
                        organizationTypes,
                        productionRoles: []
                    };
                });
        }
        return [];
    }, [data]);

    const handleMarkClick = (org: ProductionOrganizationInfoWithKey) => {

        // If production roles are not defined, fallback to organization types for backward compatibility, if those are also not defined then set to null.
        const productionRoles = org.productionRoles?.length ?
            org.productionRoles : org.organizationTypes?.length ?
                [org.organizationTypes[0]] : null;

        setIsOrganizationSaving({
            id: org.organizationReference.id,
            isSaving: true,
            productionRoles: org.productionRoles
        });

        const associatedOrganizationInput: ProductionOrganizationInfoInput = {
            name: org.name,
            organizationCategories: org.organizationCategories.map(category => {
                return {
                    lineage: {value: category.lineage?.value},
                    value: category.value
                }
            }),
            organizationTypes: org.organizationTypes,
            productionRoles: productionRoles,
            organizationReference: {
                id: org.organizationReference.id,
                eventId: org.organizationReference.eventId,
                label: org.organizationReference.label,
                type: org.organizationReference.type,
                uriPrefix: org.organizationReference.uriPrefix,
                deleted: org.organizationReference.deleted
            }
        };
        saveOrganizationToProduction({
            variables: { productionId, input: associatedOrganizationInput },
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            }
        }).then(() => {
            saveKeyOrganization(
                productionId,
                getKeyOrganizationInput(orgWithKey, undefined, undefined, {
                    ...org,
                    productionRoles,
                    isKey: true
                }),
                true
            )
                .then(() => {
                    // Delay to ensure the backend has processed the new data before refetching as new org is created
                    setTimeout(() => {
                        client.refetchQueries({ include: [ENTITY_CONSTANTS.production] }).finally(() => {
                            setIsOrganizationSaving({ id: '', isSaving: false, productionRoles: [] });
                            setSearchAllOrganization(false);
                            setQuery('');
                        });
                    }, 300);
                })
                .catch(() => {
                    setIsOrganizationSaving({
                        id: '',
                        isSaving: false,
                        productionRoles: []
                    });
                });
        });
    };

    const ItemRenderer = useCallback(
        (props) => {
            return (
                <KeyOrganizationsTitle
                    {...props}
                    showNoRole={false}
                    isLoading={isOrganizationSaving}
                    onMarkClick={(org) => handleMarkClick(org)}
                />
            );
        },
        [isOrganizationSaving]
    );

    return (
        <DataList
            emptyListElement={emptyListElement}
            id={id}
            loading={loading}
            feature={feature}
            pagination={data?.results?.hits?.page}
            items={filteredData}
            ItemRenderer={ItemRenderer}
            disableRipple={true}
            onClick={(item) => { }}
        />
    );
};
